// Synchronous decisions use field views; a durable snapshot is reserved for callers retaining inputs across an await.
// Runs own their mutable state. Catalogs in share mode are read-only: a writer must replace the map, never mutate definitions.
// A roster member aliases its row in the encounter. Raw base revisions remain non-enumerable metadata.
type Row = Record<string, unknown>;

export interface FrameCopy { encounter: Row; members: Map<string, Row> }

/** A synchronous engine run can copy fields on first access, before any page mutation can interleave.
 * Unlike the durable factories below this view MUST NOT cross an await. Scanning status families or
 * creature scalars never snapshots the unrelated members' inventories, compiles or catalogs. */
export function frameCopyOnRead(encounter: Row, members: ReadonlyMap<string, Row>,
  { catalogs = 'copy' }: { catalogs?: CatalogMode } = {}): FrameCopy {
  const rows = new WeakMap<Row, Row>();
  // Synchronous casts borrow definitions and isolate only fields they actually read/write.
  // Do not serialize a computed cluster, floor tree or catalog merely to inspect one scalar.
  const trace = catalogs === 'share'
    ? (globalThis as { ftCastInputTrace?: (kind: 'frame' | 'field', key?: string) => void }).ftCastInputTrace : undefined;
  trace?.('frame');
  const copyValue = (value: unknown): unknown => {
    if (catalogs === 'share') {
      if (Array.isArray(value)) return list(value);
      if (isRecord(value) && typeof value.toJSON !== 'function') return row(value);
      if (value === null || typeof value !== 'object')
        return dropped(value) || typeof value === 'number' && !Number.isFinite(value) ? null : value;
    }
    const snapshot = entryCopy(value, 'copy');
    return typeof snapshot === 'function' ? (snapshot as () => Row)() : snapshot;
  };
  const list = (source: unknown[]): unknown[] => {
    const result: unknown[] = new Array(source.length);
    for (let index = 0; index < source.length; index++) {
      const copy = () => isRecord(source[index]) ? row(source[index] as Row) : copyValue(source[index]);
      const at = Object.getOwnPropertyDescriptor(source, index);
      if (at && 'value' in at && at.value !== null && typeof at.value === 'object') snapshotSources.set(copy, at.value);
      lazyEntry(result, index, copy, catalogs);
    }
    return result;
  };
  const record = (source: Row): Row => {
    const result: Row = {};
    for (const key of Object.keys(source)) lazyEntry(result, key,
      () => Array.isArray(source[key]) ? list(source[key] as unknown[]) : copyValue(source[key]), catalogs);
    return result;
  };
  const row = (source: Row): Row => {
    const held = rows.get(source);
    if (held) return held;
    const result: Row = {};
    rows.set(source, result);
    for (const key of Object.keys(source)) {
      const descriptor = Object.getOwnPropertyDescriptor(source, key);
      if (descriptor && 'value' in descriptor && dropped(descriptor.value)) continue;
      lazyEntry(result, key, () => {
        trace?.('field', key);
        const value = source[key];
        if (key === 'catalog' && catalogs === 'share' && value !== null && typeof value === 'object') return value;
        if ((LAZY_ROW_LISTS.includes(key) || key === 'inventory_in_container') && Array.isArray(value)) return list(value);
        if ((key === 'catalog' || LAZY_RECORDS.includes(key)) && isRecord(value)) return record(value);
        return copyValue(value);
      }, catalogs);
    }
    return retainHeldBase(source, result);
  };
  return { encounter: row(encounter), members: new Map([...members].map(([id, source]) => [id.toUpperCase(), row(source)])) };
}

/** The encounter row lists copied per row on first read. Only at the snapshot's root: a row's own nested lists stay eager. `inventory` is the
 *  page's floor tree on the encounter (websocket/base-patch.ts repaint sets it to the same rows as encounter_inventories_arr — STEP C,
 *  fix-bug 2026-10-15: a 1,000-object floor was serialized three times per snapshot). */
const LAZY_ROW_LISTS: readonly string[] = ['encounter_creatures_arr', 'encounter_inventories_arr', 'inventory'];
/** Root records copied per KEY on first read, each array value per element: the encounter's layer map carries the floor again
 *  (compile/inventory-display.ts: inventory_equipped / inventory_unequipped wrap every floor row), and a run reading one encounter layer
 *  (read.value(ctx.encounter, …)) must not parse the rest. */
const LAZY_RECORDS: readonly string[] = ['item_layer_types'];

/** 'share': catalogs are kept by reference (the engine-frame contract above). 'copy': each catalog is a copy-on-read copy, per run. */
type CatalogMode = 'share' | 'copy';

/** JSON's own value for a non-object entry (a hole, undefined or a function → null; NaN → null) — what the eager copy held. */
const jsonValue = (value: unknown): unknown => JSON.parse(JSON.stringify(value) ?? 'null') as unknown;
const isRecord = (value: unknown): value is Row => value !== null && typeof value === 'object' && !Array.isArray(value);
/** The value each snapshot factory was taken from (an object entry) — the provenance unreadSourceRow checks. */
const snapshotSources = new WeakMap<object, object>();
/** ONE build's factories by source object, per role and mode. The page's floor rows are reachable twice at the encounter root through
 *  the SAME objects (encounter_inventories_arr and `inventory`, which websocket/base-patch.ts aliases): within one build each source object
 *  is serialized ONCE (14.5 → 9.7 MB per build on the 1,000-object capture), and every call of the shared factory still makes a fresh,
 *  independent copy — what the eager copy made of each path. The layer map's item_arr wrappers (compile/inventory-display.ts) hold deep
 *  COPIES of the rows, not the rows, so they are separate sources and are not deduplicated here. The role is part of the key: one source
 *  reached as a row, a plain entry, a row list or a lazy record gets that role's factory. Cleared when the outermost build returns; the
 *  next build reads the (mutable) source again. */
type MemoRole = 'row' | 'entry' | 'list' | 'record';
let buildMemo: Map<object, Map<string, unknown>> | null = null;
function withBuildMemo<T>(build: () => T): T {
  if (buildMemo) return build();
  buildMemo = new Map();
  try { return build(); } finally { buildMemo = null; }
}
function memoized<T>(source: object, role: MemoRole, mode: CatalogMode, make: () => T): T {
  const key = `${role}:${mode}`;
  const held = buildMemo?.get(source)?.get(key);
  if (held !== undefined) return held as T;
  const made = make();
  if (buildMemo) { let byKey = buildMemo.get(source); if (!byKey) buildMemo.set(source, byKey = new Map()); byKey.set(key, made); }
  return made;
}
/** An entry's snapshot: a ROW (an element of a listed row list) gets copy-on-read top-level keys too, anything else one serialization. */
function entryCopy(value: unknown, catalogs: CatalogMode, row = false): unknown {
  if (value === null || typeof value !== 'object') return jsonValue(value);
  return memoized(value, row ? 'row' : 'entry', catalogs, () => {
    // (a row that serializes itself — toJSON — is copied whole, as JSON would)
    const copy = row && isRecord(value) && typeof value.toJSON !== 'function' ? rowSnapshot(value, catalogs) : catalogSnapshot(value as Row, { catalogs });
    snapshotSources.set(copy, value);
    return copy;
  });
}
/** JSON drops a key whose value is undefined, a function or a symbol. */
const dropped = (value: unknown): boolean => value === undefined || typeof value === 'function' || typeof value === 'symbol';

/** Adoption keeps the raw SELECT envelope off the JSON projection. Patches replace its base;
 * pin that read-only revision without serializing its rows into every run. */
function heldBaseDescriptor(row: Row): PropertyDescriptor | undefined {
  const base = Object.getOwnPropertyDescriptor(row, '_entity_base');
  return base && !base.enumerable && isRecord(base.value)
    ? { ...base, value: { ...base.value } } : undefined;
}

/** Keep the same raw revision when a caller replaces only the projected roster. */
export function retainHeldBase<T extends Row>(source: Row, copy: T): T {
  const heldBase = heldBaseDescriptor(source);
  return heldBase ? Object.defineProperty(copy, '_entity_base', heldBase) : copy;
}

/** Every copy-on-read entry still UNREAD: host → key (as a property string — a list index "3" and 3 are one key) → its getter and snapshot. */
const unread = new WeakMap<object, Map<string, { get: () => unknown; copy: () => Row; mode: CatalogMode }>>();
/** The snapshot behind `host[key]` while that entry is unread (never read or written since it was installed — its getter still in place), else null. */
function unreadSnapshot(host: object, key: string | number): { copy: () => Row; mode: CatalogMode } | null {
  const entry = unread.get(host)?.get(String(key));
  return entry && Object.getOwnPropertyDescriptor(host, key)?.get === entry.get ? entry : null;
}
/** A checkpoint of one entry that never reads it: an UNREAD entry answers its snapshot (null for anything else). */
export type UnreadEntry = { readonly copy: () => Row; readonly mode: CatalogMode };
export function unreadEntry(host: object, key: string | number): UnreadEntry | null {
  const held = unreadSnapshot(host, key);
  return held ? { copy: held.copy, mode: held.mode } : null;
}
/** Listeners told when an entry MATERIALIZES (its first read) — a rollback checkpoint records the fresh value before anything can touch it. */
const materializeListeners = new Set<(host: object, key: string, value: unknown) => void>();
export function onMaterialize(listener: (host: object, key: string, value: unknown) => void): () => void {
  materializeListeners.add(listener);
  return () => { materializeListeners.delete(listener); };
}
/** Put a checkpointed UNREAD entry back as it was: unread again over the same snapshot (whatever the run read or wrote there since). */
export function restoreUnreadEntry(host: object, key: string | number, entry: UnreadEntry): void {
  if (unreadSnapshot(host, key)?.copy === entry.copy) return;
  lazyEntry(host, key, entry.copy, entry.mode);
}
/** Whether `host` was FROZEN (not merely sealed): a sealed host keeps its data properties writable, a frozen one does not. A host holding only
 *  accessors looks the same either way and counts as sealed (a list always has its data `length`, which answers exactly). */
function frozenHost(host: object): boolean {
  if (Object.isExtensible(host)) return false;
  for (const key of Reflect.ownKeys(host)) { const at = Object.getOwnPropertyDescriptor(host, key); if (at && 'value' in at) return !at.writable; }
  return false;
}
/** Install `key` on `host` as a copy-on-read entry: `copy` (a snapshot factory) runs on the first read; a write first just stores its value.
 *  A host sealed or frozen while the entry is unread keeps the accessor (its descriptor can no longer change) — it still answers the one
 *  materialized value, and a write to a FROZEN host throws as the frozen data property would (Codex review 2026-10-14). */
function lazyEntry(host: object, key: string | number, copy: unknown, mode: CatalogMode): void {
  if (typeof copy !== 'function') { Object.defineProperty(host, key, { value: copy, writable: true, enumerable: true, configurable: true }); return; }
  let held = false, current: unknown;
  const settle = (value: unknown): unknown => {
    held = true; current = value;
    // only THIS installation settles the property: a stale accessor (kept by a descriptor copy, or replaced since) touches nothing
    if (Object.getOwnPropertyDescriptor(host, key)?.get !== get) return value;
    if (unread.get(host)?.get(String(key))?.get === get) unread.get(host)!.delete(String(key));
    try { Object.defineProperty(host, key, { value, writable: true, enumerable: true, configurable: true }); } catch { /* sealed / frozen host */ }
    return value;
  };
  const get = (): unknown => {
    if (held) return current;
    // ownership is judged BEFORE settling (a valid settle replaces the accessor): a stale installation materializes for its caller only,
    // and is never announced — a checkpoint must not take its object for the entry's (Codex Step C recheck)
    const owner = Object.getOwnPropertyDescriptor(host, key)?.get === get;
    const value = settle((copy as () => Row)());
    if (owner) for (const listener of materializeListeners) listener(host, String(key), value);
    return value;
  };
  const set = (value: unknown): void => {
    if (frozenHost(host)) throw new TypeError(`Cannot assign to read only property '${String(key)}' of object`);
    settle(value);
  };
  Object.defineProperty(host, key, { enumerable: true, configurable: true, get, set });
  let entries = unread.get(host);
  if (!entries) unread.set(host, entries = new Map());
  entries.set(String(key), { get, copy: copy as () => Row, mode });
}
/** `host[key]`'s snapshot: an UNREAD entry's own (it IS that value — re-snapshotting must not read it only to serialize it again), else a new one. */
function snapshotOf(host: object, key: string | number, mode: CatalogMode, row = false): unknown {
  const held = unreadSnapshot(host, key);
  return held && held.mode === mode ? held.copy : entryCopy((host as Record<string | number, unknown>)[key], mode, row);
}
/** A listed row list: one copy-on-read entry per element (each a row snapshot). */
function listSnapshot(list: readonly unknown[], mode: CatalogMode): () => unknown[] {
  return memoized(list, 'list', mode, () => listFactory(list, mode));
}
function listFactory(list: readonly unknown[], mode: CatalogMode): () => unknown[] {
  const elements = Array.from({ length: list.length }, (_, index) => snapshotOf(list, index, mode, true));
  return () => {
    const copy: unknown[] = new Array(elements.length);
    elements.forEach((element, index) => lazyEntry(copy, index, element, mode));
    return copy;
  };
}
/** A LAZY_RECORDS record: one copy-on-read entry per key, an array value copied per element. */
function recordSnapshot(record: Row, mode: CatalogMode): () => Row {
  return memoized(record, 'record', mode, () => recordFactory(record, mode));
}
function recordFactory(record: Row, mode: CatalogMode): () => Row {
  const entries = Object.keys(record).filter(key => unreadSnapshot(record, key) || !dropped(record[key])).map(key => {
    const held = unreadSnapshot(record, key);
    if (held && held.mode === mode) return [key, held.copy] as const;
    const value = record[key];
    return [key, Array.isArray(value) ? listSnapshot(value, mode) : entryCopy(value, mode)] as const;
  });
  return () => {
    const copy: Row = {};
    for (const [key, entry] of entries) lazyEntry(copy, key, entry, mode);
    return copy;
  };
}
/** A listed ROW, copied per top-level key on first read (a run reading a member's scalars never parses its compile or inventory). Its own
 *  `catalog` is shared or copied by the mode, like every other catalog. */
export function rowSnapshot(row: Row, mode: CatalogMode = 'share'): () => Row {
  return withBuildMemo(() => rowFactory(row, mode));
}
function rowFactory(row: Row, mode: CatalogMode): () => Row {
  const heldBase = heldBaseDescriptor(row);
  // a shared catalog is the row's CURRENT catalog object (read — and so materialized — first when it is itself an unread copy)
  const entries = Object.keys(row).filter(key => unreadSnapshot(row, key) || !dropped(row[key])).map(key => [key,
    key === 'catalog' && mode === 'share' && row[key] !== null && typeof row[key] === 'object' ? { shared: row[key] }
      : snapshotOf(row, key, mode)] as const);
  return () => {
    const copy: Row = {};
    for (const [key, entry] of entries) {
      if (entry !== null && typeof entry === 'object' && 'shared' in entry) copy[key] = entry.shared;
      else lazyEntry(copy, key, entry, mode);
    }
    return heldBase ? Object.defineProperty(copy, '_entity_base', heldBase) : copy;
  };
}

/** Whether `list[index]` is still an UNREAD copy-on-read entry (never read or written since its copy was made). */
export function unreadRow(list: readonly unknown[], index: number): boolean {
  return unreadSnapshot(list, index) !== null;
}
/** The SOURCE row behind an unread `list[index]` — for a scan that needs only that row's facts, without parsing the copy — and only when
 *  `source[index]` is, as a plain value, the very object that entry's snapshot was taken from (so a list the run replaced, or a source that
 *  moved, never answers for it). The caller guarantees the source is not mutated in place meanwhile, and reads its facts as JSON would
 *  (a key holding undefined / a function is not there). undefined = read `list[index]` itself. */
export function unreadSourceRow(list: readonly unknown[], index: number, source: unknown): object | undefined {
  const held = unreadSnapshot(list, index);
  if (!held || !Array.isArray(source)) return undefined;
  const at = Object.getOwnPropertyDescriptor(source, index);
  const origin = snapshotSources.get(held.copy);
  return at && 'value' in at && origin !== undefined && at.value === origin ? origin : undefined;
}

/** Serialize mutable state once, without repeatedly copying catalog definitions. Works for nested catalogs too. */
export function catalogSnapshot(row: Row, options: { catalogs?: CatalogMode } = {}): () => Row {
  return withBuildMemo(() => catalogFactory(row, options.catalogs ?? 'share'));
}
function catalogFactory(row: Row, mode: CatalogMode): () => Row {
  const heldBase = heldBaseDescriptor(row);
  type Path = { parent: Path | null; key: string };
  const paths = new WeakMap<object, Path | null>();
  const catalogs: Array<{ path: string[]; value: object; entries: Array<[string, unknown]> | null }> = [];
  const lists: Array<{ key: string; copy: () => unknown }> = [];
  let root = true;
  const text = JSON.stringify(row, function (key, value: unknown): unknown {
    if (root) { root = false; if (value && typeof value === 'object') paths.set(value, null); return value; }
    if (value && typeof value === 'object') {
      const parent = paths.get(this as object) ?? null;
      if (key === 'catalog') {
        const path = [key];
        for (let at = parent; at; at = at.parent) path.push(at.key);
        // a copied catalog keeps JSON's own shape: an object map per entry (dropped entries as JSON drops them), anything else eager
        const entries = mode === 'copy' && !Array.isArray(value)
          ? Object.keys(value).filter(id => unreadSnapshot(value, id) || JSON.stringify((value as Row)[id]) !== undefined)
            .map(id => [id, snapshotOf(value, id, mode)] as [string, unknown])
          : null;
        if (mode === 'copy' && !entries) { paths.set(value, { parent, key }); return value; }
        catalogs.push({ path: path.reverse(), value, entries });
        return null;
      }
      // kept as null in the text so the key keeps its position
      if (this === row && Array.isArray(value) && LAZY_ROW_LISTS.includes(key)) { lists.push({ key, copy: listSnapshot(value, mode) }); return null; }
      if (this === row && isRecord(value) && LAZY_RECORDS.includes(key) && typeof value.toJSON !== 'function') { lists.push({ key, copy: recordSnapshot(value, mode) }); return null; }
      paths.set(value, { parent, key });
    }
    return value;
  });
  return () => {
    const copy = JSON.parse(text) as Row;
    for (const { key, copy: listOrRecord } of lists) copy[key] = listOrRecord();
    for (const { path, value, entries } of catalogs) {
      let host = copy;
      for (let i = 0; i < path.length - 1; i++) host = host[path[i]!] as Row;
      if (!entries) { host.catalog = value; continue; }
      const catalog: Row = {};
      for (const [id, entry] of entries) lazyEntry(catalog, id, entry, mode);
      host.catalog = catalog;
    }
    return heldBase ? Object.defineProperty(copy, '_entity_base', heldBase) : copy;
  };
}

/** A factory of fresh, independent copies of `encounter` + `members` (keys upper-cased, as the frame's ctx.members always were). */
export function frameSnapshot(encounter: Row, members: ReadonlyMap<string, Row>): () => FrameCopy {
  const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : [];
  const at = new Map<unknown, number>();
  roster.forEach((row, i) => { if (row !== null && typeof row === 'object' && !at.has(row)) at.set(row, i); });
  const fresh = catalogSnapshot(encounter);
  // a member held by the roster is aliased by index; any other member is serialized on its own
  const sources = [...members].map(([id, row]) => [String(id ?? '').trim().toUpperCase(), at.has(row) ? at.get(row)! : catalogSnapshot(row)] as const);
  return () => {
    const copy = fresh();
    const rows = copy.encounter_creatures_arr as Row[];
    return { encounter: copy, members: new Map(sources.map(([id, source]) => [id, typeof source === 'number' ? rows[source]! : source()])) };
  };
}
